/**
 * ReminderConfigTab - Program-level reminder escalation configuration
 * Tab content for program setup/management
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Settings, Save, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { EscalationTimeline } from './EscalationTimeline';
import { useReminderConfig } from '@/hooks/useEcmReminders';
import type { ProgramReminderConfigUpdate } from '@/services/api/ecmReminders';

interface ReminderConfigTabProps {
  programId: number;
  programName?: string;
}

export const ReminderConfigTab: React.FC<ReminderConfigTabProps> = ({ programId, programName }) => {
  const { config, loading, error, updateConfig, refetch } = useReminderConfig(programId);

  // Local form state
  const [formData, setFormData] = useState<ProgramReminderConfigUpdate>({});
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Sync form data when config loads
  useEffect(() => {
    if (config) {
      setFormData({
        escalation_interval_hours: config.escalation_interval_hours,
        max_escalation_tier: config.max_escalation_tier,
        monthly_contact_start_day: config.monthly_contact_start_day,
        monthly_contact_warn_day: config.monthly_contact_warn_day,
        monthly_contact_urgent_day: config.monthly_contact_urgent_day,
        reassessment_window_days: config.reassessment_window_days,
        default_channels: config.default_channels,
      });
      setIsDirty(false);
    }
  }, [config]);

  const updateField = (field: keyof ProgramReminderConfigUpdate, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    // Validate day ordering
    const start = formData.monthly_contact_start_day || 15;
    const warn = formData.monthly_contact_warn_day || 20;
    const urgent = formData.monthly_contact_urgent_day || 25;

    if (start > warn || warn > urgent) {
      toast.error('Day ordering must be: Start Day <= Warn Day <= Urgent Day');
      return;
    }

    setSaving(true);
    try {
      const result = await updateConfig(formData);
      if (result) {
        toast.success('Reminder configuration updated');
        setIsDirty(false);
      } else {
        toast.error('Failed to save configuration');
      }
    } catch {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !config) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-gray-500">{error}</p>
        <Button variant="outline" size="sm" onClick={refetch} className="mt-3">
          Retry
        </Button>
      </div>
    );
  }

  const startDay = formData.monthly_contact_start_day || 15;
  const warnDay = formData.monthly_contact_warn_day || 20;
  const urgentDay = formData.monthly_contact_urgent_day || 25;
  const escalationHours = formData.escalation_interval_hours || 48;
  const maxTier = formData.max_escalation_tier || 4;
  const windowDays = formData.reassessment_window_days || 14;
  const channels = formData.default_channels || ['in_app'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-600" />
            Program Reminder Configuration
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Configure when and how reminders escalate for {programName || 'this program'}.
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={!isDirty || saving}
        >
          <Save className="w-4 h-4 mr-1" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Configuration Form */}
        <div className="space-y-6">
          {/* Monthly Contact Thresholds */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-semibold">Monthly Contact Reminders</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Start Day (Tier 1): <span className="font-bold text-blue-600">Day {startDay}</span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={28}
                  value={startDay}
                  onChange={(e) => updateField('monthly_contact_start_day', Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Day 1</span>
                  <span>Day 28</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Warn Day (Tier 2): <span className="font-bold text-orange-600">Day {warnDay}</span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={28}
                  value={warnDay}
                  onChange={(e) => updateField('monthly_contact_warn_day', Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Urgent Day (Tier 3): <span className="font-bold text-red-600">Day {urgentDay}</span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={28}
                  value={urgentDay}
                  onChange={(e) => updateField('monthly_contact_urgent_day', Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500"
                />
              </div>
            </CardContent>
          </Card>

          {/* Escalation Settings */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-semibold">Escalation Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Escalation Interval: <span className="font-bold">{escalationHours} hours</span>
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  Time between escalation tiers when a reminder goes unacknowledged.
                </p>
                <input
                  type="range"
                  min={12}
                  max={168}
                  step={12}
                  value={escalationHours}
                  onChange={(e) => updateField('escalation_interval_hours', Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>12h</span>
                  <span>7 days</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Escalation Tier
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map((tier) => (
                    <Button
                      key={tier}
                      type="button"
                      variant={maxTier === tier ? 'primary' : 'outline'}
                      size="sm"
                      onClick={() => updateField('max_escalation_tier', tier)}
                    >
                      Tier {tier}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {maxTier >= 4 ? 'Includes supervisor escalation' : 'No supervisor escalation'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reassessment Reminder Window: <span className="font-bold">{windowDays} days</span>
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  Days before 90-day review to start sending reassessment reminders.
                </p>
                <input
                  type="range"
                  min={7}
                  max={30}
                  value={windowDays}
                  onChange={(e) => updateField('reassessment_window_days', Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>7 days</span>
                  <span>30 days</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Default Channels */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-semibold">Default Notification Channels</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                {['in_app', 'email', 'sms'].map((ch) => {
                  const isActive = channels.includes(ch);
                  const labels: Record<string, string> = {
                    in_app: 'In-App',
                    email: 'Email',
                    sms: 'SMS',
                  };
                  return (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => {
                        const newChannels = isActive
                          ? channels.filter((c) => c !== ch)
                          : [...channels, ch];
                        if (newChannels.length === 0) return; // Must have at least one
                        updateField('default_channels', newChannels);
                      }}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {labels[ch]}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Tier 3+ always uses all channels regardless of this setting.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Right: Live Timeline Preview */}
        <div>
          <EscalationTimeline
            startDay={startDay}
            warnDay={warnDay}
            urgentDay={urgentDay}
            escalationHours={escalationHours}
            maxTier={maxTier}
          />
        </div>
      </div>
    </div>
  );
};
